import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Modal, IconButton, Button } from '@mui/material';
import { addDoc, collection, updateDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { negotiation as negotiationDoc } from '../../api/firebase/firestore/negotiation';
import { declineCaseSchema } from '../../api/firebase/firestore/negotiation/validation';
import { sendNoticeMailFromClientAgent } from '../../api/sendgrid';
import { causeChoice } from '../../constants/CheckboxList';
import {
  black,
  blueButton,
  primaryButton,
  tertiaryButtonBackground
} from '../../constants/Colors';
import { chatType, negotiationStatus } from '../../constants/enum';
import { throwError } from '../../modules/error';

import CustomCheckbox from './CustomCheckbox';
import CustomTextfield from './CustomTextfield';

import type { NegotiationType } from '../../api/firebase/firestore/negotiation/negotiationType';
import type { User } from 'firebase/auth';

const styles = {
  blueButton,
  modal: {
    '@media screen and (max-width:480px)': {
      margin: '0 16px'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: 2
  },
  paper: {
    '@media screen and (max-width:480px)': {
      padding: '24px 16px',
      width: '100%'
    },
    backgroundColor: 'white',
    padding: '24px 0',
    position: 'relative',
    width: '480px'
  },
  primaryButton,
  tertiaryButtonBackground
};

interface Props {
  open: boolean;
  handleClose: () => void;
  negotiation: NegotiationType | null;
  currentUser: User | null | undefined;
  init: () => Promise<void>;
}

export interface DeclineCaseForm {
  cause: boolean[];
  comment: string;
}

const DeclineModal = (props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const { open, handleClose, negotiation, currentUser, init } = props;

  const {
    control,
    watch,
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<DeclineCaseForm>({
    resolver: yupResolver(declineCaseSchema)
  });

  const checkboxArray = (value: boolean[]): number[] => {
    const array: number[] = [];
    value.forEach((elm: boolean, key) => {
      if (elm) {
        array.push(key);
      }
    });
    return array;
  };

  const choices = causeChoice();

  const addLabel = (watchCause: number[]): Array<string | undefined> =>
    watchCause.map((value: number) => {
      const choice = choices.find((c) => c.value === value);
      return choice ? choice.label : '';
    });

  useEffect(() => {
    reset({
      cause: [],
      comment: ''
    });
  }, [open]);

  const onSubmit = async (): Promise<void> => {
    try {
      if (!currentUser?.uid || !negotiation?.negotiationId) {
        throwError(new Error('User ID or negotiation ID is missing.'));
        return;
      }

      const negotiationRef = negotiationDoc(negotiation.negotiationId);
      const data = {
        status: negotiationStatus.decline
      };
      await updateDoc(negotiationRef, data);

      const chatData = {
        chat: t('message.declined'),
        created: new Date(),
        senderId: currentUser.uid,
        type: chatType.system
      };

      const chatCollection = collection(negotiationRef, 'chats');
      const chat = await addDoc(chatCollection, chatData);
      await updateDoc(chat, {
        chatId: chat.id
      });

      const watchCause = checkboxArray(watch('cause'));
      const watchComment = watch('comment');

      const causeLabels = addLabel(watchCause);

      const causeText = causeLabels.join('\n・');
      const commentText = `失注要因\n・${causeText}\n\nコメント\n${watchComment}`;

      if (!watchCause || !watchComment) {
        throwError(new Error('Cause or comment is undefined.'));
        return;
      }

      const additionalChat = {
        chat: commentText,
        created: new Date(),
        senderId: currentUser.uid,
        type: chatType.user
      };

      const additinalChatCollection = collection(negotiationRef, 'chats');
      const addChat = await addDoc(additinalChatCollection, additionalChat);

      await updateDoc(addChat, {
        chatId: addChat.id
      });

      const refusalData = {
        cause: watchCause,
        chatId: chat.id,
        comment: watchComment,
        created: new Date(),
        senderId: currentUser.uid
      };

      const refusalsCollection = collection(negotiationRef, 'refusals');
      const refusal = await addDoc(refusalsCollection, refusalData);

      await updateDoc(refusal, {
        refusalId: refusal.id
      });

      sendNoticeMailFromClientAgent(
        negotiation?.publisherMainUid ?? '',
        negotiation?.publisherMember ?? [],
        commentText,
        negotiation,
        '辞退',
        currentUser.uid
      );

      handleClose();
      await init();
    } catch (e) {
      throwError(e);
    }
  };

  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('cause') === undefined ||
        '' ||
        Boolean(errors.cause) ||
        Boolean(errors.comment)
      );
    }
    return false;
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} sx={styles.modal}>
        <Typography sx={styles.paper}>
          <Typography
            sx={{
              '@media screen and (max-width:480px)': {
                p: 0
              },
              p: '0 40px'
            }}
          >
            <IconButton
              edge="end"
              color="inherit"
              aria-label="close"
              onClick={handleClose}
              style={{
                color: '#757575',
                position: 'absolute',
                right: '40px',
                top: '24px'
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ color: black, fontSize: '22px', mb: '16px' }}>
              辞退
            </Typography>
            <CustomCheckbox
              labelText={'失注要因'}
              isRequired={true}
              requiredText={t('form.required')}
              list={causeChoice()}
              error={Boolean(errors.cause)}
              helperText={errors.cause?.message}
              control={control}
              watchDecline={watch}
              registerDecline={register}
              formMb="narrow"
              name="cause"
            />
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.comment)}
                  helperText={errors.comment?.message}
                  isRequired={false}
                  labelText={t('message.comment')}
                  placeholder={t('form.placeholder')}
                  multiline
                  rows={7}
                  fieldType="textareaRow7"
                  formMb="narrow"
                />
              )}
              control={control}
              name="comment"
            />
            <Button
              fullWidth
              sx={[styles.primaryButton, { mt: '24px' }]}
              disabled={buttonDisabledFlag()}
              onClick={() => {
                void (async () => {
                  setFirstSubmit(true);
                  await handleSubmit(onSubmit)();
                })();
              }}
            >
              送信する
            </Button>
          </Typography>
        </Typography>
      </Modal>
    </>
  );
};

export default DeclineModal;
